'use client';
import React from 'react';
/**
 * BookingModalContext - React context for managing global booking modal state.
 *
 * This module provides a context and hooks for controlling a global booking modal
 * that can be triggered from anywhere in the application. It's typically used with
 * the GlobalBookingModal component to provide a single, reusable modal instance.
 *
 * @module
 */

import type { BookingModalRequest } from '@marketing-optimizer/ontology';
import type { BookingModalEntry, BookingModalOpen } from './BookingModal.types';
import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';

/** Context value type for the booking modal. */
interface BookingModalContextValue {
  isOpen: boolean;
  /**
   * What every "Talk to us" calls: open the modal on the "Talk to Nathan"
   * form. With `report`, the report's form (only the optional note). Its
   * footer switches to the calendar without submitting.
   */
  openModal: (open: BookingModalOpen) => void;
  /**
   * Open straight on Nathan's calendar, with no email step (HAL-2083): the
   * report's "Book a 15m call" link. Its footer pill leads back to the form.
   */
  openCalendar: (open: BookingModalOpen) => void;
  closeModal: () => void;
  /** Set by `openModal` and `openCalendar`, cleared by `openRequest`. */
  entry?: BookingModalEntry;
  request?: BookingModalRequest;
  requestKey: number;
  openRequest: (request: BookingModalRequest) => void;
}

const BookingModalContext = createContext<BookingModalContextValue | null>(null);

/**
 * BookingModalProvider - Context provider component for the booking modal.
 *
 * This provider should be placed high in the component tree (typically in the root layout)
 * to make the booking modal context available to all child components.
 *
 * @component
 * @param {Object} props - Component props
 * @param {ReactNode} props.children - Child components that will have access to the booking modal context
 * @returns {React.ReactElement} Provider wrapper around children
 *
 * @example
 * ```tsx
 * export default function RootLayout({ children }: { children: ReactNode }) {
 *   return (
 *     <html>
 *       <body>
 *         <BookingModalProvider>
 *           {children}
 *           <GlobalBookingModal />
 *         </BookingModalProvider>
 *       </body>
 *     </html>
 *   );
 * }
 * ```
 */
export function BookingModalProvider({ children }: { children: ReactNode }): React.ReactElement {
  const [isOpen, setIsOpen] = useState(false);

  const [request, setRequest] = useState<BookingModalRequest>();
  const [entry, setEntry] = useState<BookingModalEntry>();
  const [requestKey, setRequestKey] = useState(0);
  const open = useCallback((start: BookingModalEntry['start'], value: BookingModalOpen) => {
    setRequest(undefined);
    setEntry({ placement: value.placement, start, ...(value.report && { report: value.report }) });
    setRequestKey((key) => key + 1);
    setIsOpen(true);
  }, []);
  const openModal = useCallback((value: BookingModalOpen) => open('form', value), [open]);
  const openCalendar = useCallback((value: BookingModalOpen) => open('calendar', value), [open]);
  const openRequest = useCallback((value: BookingModalRequest) => {
    setRequest(value);
    setEntry(undefined);
    setRequestKey((key) => key + 1);
    setIsOpen(true);
  }, []);
  const closeModal = useCallback(() => setIsOpen(false), []);

  return (
    <BookingModalContext.Provider
      value={{ isOpen, openModal, openCalendar, closeModal, entry, request, requestKey, openRequest }}
    >
      {children}
    </BookingModalContext.Provider>
  );
}

/**
 * useBookingModal - Hook to access and control the global booking modal.
 *
 * This hook provides access to the booking modal's state and control functions.
 * It must be used within a component that is wrapped by BookingModalProvider.
 *
 * @hook
 * @returns {BookingModalContextValue} Object containing modal state and control functions
 * @throws {Error} If hook is used outside of BookingModalProvider
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { openModal } = useBookingModal();
 *
 *   return (
 *     <button onClick={() => openModal({ placement: 'hero' })}>
 *       Talk to us
 *     </button>
 *   );
 * }
 * ```
 */
export function useBookingModal(): BookingModalContextValue {
  const context = useContext(BookingModalContext);
  if (!context) {
    throw new Error('useBookingModal must be used within a BookingModalProvider');
  }
  return context;
}

/**
 * The same context without the provider requirement. For components that take
 * an `onTalkToUs` override and only reach for the modal as their default: a
 * section rendered on its own (a spec, a fixture, a future page without the
 * modal) gets `null` rather than a throw.
 */
export function useOptionalBookingModal(): BookingModalContextValue | null {
  return useContext(BookingModalContext);
}
